import React, { useEffect, useState } from "react";

const API_BASE_URL = "";

const emptyForm = {
  name: "",
  from: "07:00",
  to: "08:00",
};

const TimeSlots = () => {
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const getToken = () => localStorage.getItem("indiaboatsAdminToken");

  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${getToken()}`,
  });

  const fetchSlots = async () => {
    const response = await fetch(`${API_BASE_URL}/api/time-slots/slots`);

    if (!response.ok) {
      throw new Error("Failed to load time slots");
    }

    const result = await response.json();
    setSlots(Array.isArray(result) ? result : []);
  };

  useEffect(() => {
    const load = async () => {
      try {
        await fetchSlots();
      } catch (error) {
        console.error("Time slots fetch error:", error);
        alert("Failed to load time slots");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const startEdit = (slot) => {
    setEditingId(slot._id);
    setForm({
      name: slot.name || "",
      from: slot.from || "07:00",
      to: slot.to || "08:00",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSave = async () => {
    if (!form.from || !form.to) {
      alert("Please enter From and To times");
      return;
    }

    try {
      setSaving(true);

      const url = editingId
        ? `${API_BASE_URL}/api/time-slots/slots/${editingId}`
        : `${API_BASE_URL}/api/time-slots/slots`;

      const response = await fetch(url, {
        method: editingId ? "PUT" : "POST",
        headers: authHeaders(),
        body: JSON.stringify(form),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to save time slot");
      }

      await fetchSlots();
      resetForm();
      alert(result.message || "Time slot saved");
    } catch (error) {
      console.error("Save time slot error:", error);
      alert(error.message || "Failed to save time slot");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slot) => {
    if (
      !window.confirm(
        `Delete "${slot.label}"? Related yacht prices for this slot will also be removed.`
      )
    ) {
      return;
    }

    try {
      setSaving(true);

      const response = await fetch(
        `${API_BASE_URL}/api/time-slots/slots/${slot._id}`,
        {
          method: "DELETE",
          headers: authHeaders(),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to delete time slot");
      }

      await fetchSlots();

      if (editingId === slot._id) {
        resetForm();
      }
    } catch (error) {
      console.error("Delete time slot error:", error);
      alert(error.message || "Failed to delete time slot");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="cms-page">
        <div className="cms-page-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="cms-page">
      <div className="cms-page-header">
        <div>
          <h1>Boat Time Management</h1>
          <p>
            Create named time ranges in 24-hour format. These slots can be
            priced per boat in Yacht Prices.
          </p>
        </div>
      </div>

      <div className="cms-section-card">
        <div className="cms-section-card-header">
          <h2>{editingId ? "Update Time Slot" : "Create Time Slot"}</h2>
          <p>Enter from and to times as 00:00 to 23:59. Name is optional.</p>
        </div>

        <div className="cms-form-grid cms-form-grid-4">
          <div className="cms-form-group">
            <label>Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) =>
                setForm((previous) => ({ ...previous, name: e.target.value }))
              }
              placeholder="e.g. Morning slot"
            />
          </div>

          <div className="cms-form-group">
            <label>From (24-hour)</label>
            <input
              type="time"
              value={form.from}
              onChange={(e) =>
                setForm((previous) => ({ ...previous, from: e.target.value }))
              }
            />
          </div>

          <div className="cms-form-group">
            <label>To (24-hour)</label>
            <input
              type="time"
              value={form.to}
              onChange={(e) =>
                setForm((previous) => ({ ...previous, to: e.target.value }))
              }
            />
          </div>
        </div>

        <p className="cms-inquiries-empty" style={{ marginTop: 12 }}>
          Preview: {form.name ? `${form.name} (` : ""}
          {form.from || "--:--"} - {form.to || "--:--"}
          {form.name ? ")" : ""}
        </p>

        <div className="cms-bottom-actions">
          <button
            type="button"
            className="cms-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : editingId ? "Save Changes" : "Save Time Slot"}
          </button>
          {editingId ? (
            <button
              type="button"
              className="cms-upload-btn"
              onClick={resetForm}
              disabled={saving}
            >
              Cancel Update
            </button>
          ) : null}
        </div>
      </div>

      <div className="cms-section-card">
        <div className="cms-section-card-header">
          <h2>Time Slots</h2>
          <p>Update loads the form above. Delete asks for confirmation first.</p>
        </div>

        {slots.length === 0 ? (
          <p className="cms-inquiries-empty">No time slots created yet.</p>
        ) : (
          <div className="cms-enquiries-table-wrap">
            <table className="cms-enquiries-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Label</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {slots.map((slot, index) => (
                  <tr key={slot._id}>
                    <td>{index + 1}</td>
                    <td>{slot.name || "—"}</td>
                    <td>{slot.from || "—"}</td>
                    <td>{slot.to || "—"}</td>
                    <td>{slot.label}</td>
                    <td>
                      <div className="cms-item-row-actions">
                        <button
                          type="button"
                          className="cms-upload-btn"
                          onClick={() => startEdit(slot)}
                        >
                          Update
                        </button>
                        <button
                          type="button"
                          className="cms-delete-btn"
                          onClick={() => handleDelete(slot)}
                          disabled={saving}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeSlots;
